import { FC, useEffect, useState, useMemo, memo, useCallback, useRef, ReactNode, lazy } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { Nullable, Project, API, ParticipateCompetitions, Lang, Designer } from '@brg/api';
import { useProfile, bProfile } from '@brg/auth';
import { FlexBox } from '@brg/ui';
import InfiniteScroll from 'react-infinite-scroll-component';

import { ChannelsGrid, GridWrapperNew, ProjectsGridNew, TeamGrid } from '../../shared/components/grids';
import ProjectCardNew from '../../shared/components/projectCard/projectCardNew';
import { computeProject } from '../../shared/helpers/types/computed/ComputedProject';
import { bProject } from '../utils';
import useQueryStateOrigin from "../../shared/hooks/useQueryState";
import { useTranslation } from 'react-i18next';
import { ProgressBar } from 'shared/icons/Progress';
import styled, { useTheme } from 'styled-components';
import { AppTester } from "supporting";
import { BookCard } from '_deziiign/books/core/components/bookCard';
import { computeBook, computeBookProject } from '_deziiign/books/core/components/helper/types/computed/ComputedBook';
import { EmptyCardsLoading } from 'shared/components/CardsLoading/EmptyCards';
import { SkeletonCard, SkeletonCardMap } from 'shared/components/SkeletonCard/SkeletonCard';
import { ProjectDelegate } from 'project-delegate';
import { TOwnerRights } from 'shared/pages/exploreHseContextsNew/exploreHseContextsNew';
import { ProjectsGridActions, ProjectsGridSelector } from "../../shared/reducers/ProjectsGridSlice";
import { fetchProjectsGrid, fetchProjectsGridMore } from "../../shared/actions/projectsGridActions";
import { ComputedTeam, computeTeam } from "../../shared/helpers/types/computed/ComputedTeam";
import { DesignerCompetitionsTab } from "../../shared/pages/designer/components/tabs/designerCompetitionsTab";
import { CombinedEvents } from "../../shared/helpers/projectEvents";
import { DraftsGrid } from "../../shared/pages/designer/components/draftsGrid";
import { DesignerTab } from "../../shared/constants/designerTab";
import { ModerationType } from "../../shared/pages/teams/const/ModerationTypes";
import CompetitionGrid from "../../_competition/pages/AllCompetitionsPage/CompetitionGrid";
import { BookDelegate } from "../../_deziiign/books/core/book-delegate";
import WideCoverSlice, { WideCoverSelector } from 'shared/reducers/WideCoverSlice';
import { EmptyCategory } from 'shared/components/EmptyCategory/EmptyCategory';
import { ShopCard } from 'shared/pages/Shop/ShopCard';
import { CreateLinkTo, LinkTo } from 'shared/constants/linkTo';
import { DesignerDiscountTab } from 'shared/pages/designer/components/tabs/designerDiscountstab';
import { useParams, useRouteMatch as useRouteMatchOrigin } from "react-router-dom";
import { PeoplePageGrid } from 'shared/pages/context/support/Grid';
import { mapProjectToMapObject } from 'shared/pages/ResearchPage/Research/Frames/FramesSsr/mapProjectToMapObject';
import { isServer } from 'shared/constants/isServer';
import { useParamsSsr, useRouteMatchSsr } from 'shared/hooks/useRouteMatchSsr';
import { ProfileFoldersProps, ProfileFoldersSelector } from 'shared/reducers/ProfileFoldersSlice';
import { Placement } from 'shared/components/tooltips';
import useEffectSkipFirst from 'core/hooks/useEffectSkipFirst';

const useRouteMatch = isServer ? useRouteMatchSsr : useRouteMatchOrigin
const useQueryState = isServer ? () => [{ teachersOnly: false, showcase: false, folderId: false }] : useQueryStateOrigin
const PopupInfoNodeGrid = isServer ? FlexBox : lazy(() => import('shared/pages/ResearchPage/Research/Components/Popup/stylesSsr/PopupInfoNodeGrid'))
const PopUpInfoNode = isServer ? FlexBox : lazy(() => import('../../shared/pages/ResearchPage/Research/Components/Popup/PopUpInfoNode'))
const usedUseParams = isServer ? useParamsSsr : useParams
const useParamsProps = isServer ? LinkTo.designer : undefined

export const StyledLoading = styled.div`
  margin-bottom: 40px;
  padding: 10px;
  font-size: 60px;
  line-height: 1;
  color: white;
  text-align: center;
`;

export const ShowMoreWrapper = styled(FlexBox)`
  margin-left: auto;
  margin-right: auto;
  border: 1px solid ${p => p.theme.progressbar.borderColor};
  border-radius: 3px;
  padding: 12px 48px;
`;

const OffsetBlock = styled.div<{ display: boolean }>`
  width: 100%;
  margin-top: 64px;
  display: ${p => p?.display ? 'inline-block' : 'none'};
`;

const ScrollWrapper = styled(FlexBox)`
  .infinite-scroll-component {
    overflow: visible !important;
  }
`

type Tabs = {
  [key: string]: string[] | string | null;
}

export interface ProjectsButchControllerProps<T = Project> {
  filter: any;
  onProjectsLoaded?: (projects: T[]) => void;
  selectable?: boolean;
  onSelect?: (projectId: string) => void;
  withoutGoToProject?: boolean;
  withoutRepost?: boolean;
  selectedProjects?: string[];
  isParticipate?: string[];
  modal?: boolean;
  linkPrefix?: string;
  projectsCount?: number;
  isLoading?: Function;
  isSelectRightNeed?: boolean;
  columns?: number;
  isUnload?: boolean;
  preloadedTabs?: Tabs[];
  emptyMsgComponent?: ReactNode;
  currentTab: string;
  currentCategory: string;
  contextName?: string;
  isPageWithoutTabs?: boolean;
  emptyMsgText?: string;
  showCompetitionPoint?: boolean;
  isChannelParticipant?: boolean;
  isTeamProjects?: boolean;
  isDisabled?: boolean;
  canMakeImageForTeam?: TOwnerRights;
  isMainPage?: boolean;
  isProfile?: boolean;
  isPeoplePage?: boolean;
  isCompetitions?: boolean;
  handlers?: CombinedEvents;
  moderationType?: ModerationType;
  loading?: boolean;
  teamId?: string;
  accountId?: string;
  mobileColumns?: number;
  scrollableTarget?: any;
  isMe?: number;
  scrollStyles?: any;
  showOpa?: boolean;
  channelOwnerId?: string;
  isChannel?: boolean;
  isOpaCard?: boolean;
  isMobileMoreVisible?: boolean;
  placement?: Placement;
  onScroll?: (e: any) => void;
}

const ProjectsButchControllerUltimate: FC<ProjectsButchControllerProps> = ({
  filter,
  selectable = false,
  modal = false,
  onSelect = () => undefined,
  withoutGoToProject,
  selectedProjects = [],
  isParticipate = [],
  linkPrefix,
  columns,
  isPageWithoutTabs,
  withoutRepost,
  contextName,
  teamId,
  accountId,
  currentTab,
  scrollStyles,
  currentCategory,
  showCompetitionPoint,
  canMakeImageForTeam,
  isProfile,
  isPeoplePage,
  isCompetitions,
  isMainPage,
  handlers,
  mobileColumns,
  loading,
  emptyMsgComponent,
  isChannelParticipant,
  emptyMsgText,
  scrollableTarget,
  isMe,
  showOpa,
  isOpaCard,
  isChannel,
  channelOwnerId,
  placement,
  onScroll
}) => {
  const designer = useProfile();
  const theme = useTheme();
  const isMapPage = useRouteMatch('/map');
  const isFeedPage = useRouteMatch('/feed');

  const [{ teachersOnly, showcase = false, folderId }] = useQueryState();
  const { id: designerId } = usedUseParams(useParamsProps)

  const dispatch = useDispatch();
  const isAgeyTomesh = AppTester.isAgeytomesh();
  const isMedia = AppTester.isMediiia();
  const isLMSHSE = AppTester.isLMSHSE();
  const isGreenAward = AppTester.isGreenAward();
  const isDafes = AppTester.isGeoDafes();
  const isHH = AppTester.isContext('hsehh');
  const isHseCreative = AppTester.isLMSHSECreative();
  const isLectureMedia = AppTester.isLectureMediiia();
  const isSchool = AppTester.isSchool()
  const isStart = AppTester.isStartHSE();
  const isAcademy = AppTester.isAcademy();
  const isMap = !!useRouteMatch('/map');
  const isExactPage = !!useRouteMatch('/');
  const isMainPageHseSpace = AppTester.isContext('hsespace') && isExactPage;
  const isHseSpaceDesignerPage = AppTester.isContext('hsespace') && !!useRouteMatch('/designer/:id')

  const { t, i18n, i18n: { language } } = useTranslation();
  const [time, setTime] = useState(Date.now());
  const [initialLoaded, setInitialLoaded] = useState(false)
  const initialLoadedRef = useRef(false)
  const [firstPageLoaded, setFirstPageLoaded] = useState(false)

  const prevUserId = useRef<any>(null);

  const hasWideCover = useSelector(WideCoverSelector)?.project;
  const preloadedTabs = useSelector(ProjectsGridSelector)?.tabs;
  const preloadedTabsContext = useSelector(ProjectsGridSelector)?.context;
  const data = useSelector(ProjectsGridSelector)?.data?.[currentCategory]?.[currentTab];
  const fetchingTabs = useSelector(ProjectsGridSelector)?.tabsLoading?.[currentCategory]?.[currentTab];
  const fetchingMore = useSelector(ProjectsGridSelector)?.fetchingMore;
  const prevTeam = useSelector(ProjectsGridSelector)?.prevTeam;
  const currentFolder = useSelector(ProfileFoldersSelector)?.currentFolder;
  //@ts-ignore
  const currentTeam = useSelector((state) => state?.organizations)?.team;
  
  const isSuperPuperGames = useMemo(
    () => ((currentTeam?.contextName  === 'superpupergames-mediiia'))
  , [currentTeam]);

  const hideProjects = useMemo(() => {
    return ([ProfileFoldersProps.create, ProfileFoldersProps.edit]?.includes(currentFolder) || folderId) && isProfile
  }, [folderId, isProfile, currentFolder])

  const reloadAllTabs = useCallback(() => {
    setTime(Date.now());
    handlers?.onUpdate?.();
  }, [handlers])

  const { isFinalPage, paging, entityType, isDrafts } = data || {};

  useEffect(() => {
    if (isProfile && prevUserId.current && prevUserId.current !== designerId) {
      dispatch(ProjectsGridActions.resetProjects())
    }

    prevUserId.current = designerId
  }, [prevUserId, designerId, dispatch, isProfile])

  const loadProjects = useCallback(() => {
    dispatch(fetchProjectsGrid({
      filter,
      isChannel,
      channelOwnerId,
      currentCategory,
      contextName,
      currentTab,
      teamId,
      accountId,
      designerId,
      teachersOnly,
      isMe: (isMe || !!isFeedPage),
      isHseSpaceDesignerPage,

      //todo [МУСОР] удалить
      isLMSHSE,
      isProfile,
      isPeoplePage,
      isCompetitions,
      isLectureMedia,
      isSchool,
    }))
  }, [
    filter,
    isChannel,
    channelOwnerId,
    currentCategory,
    contextName,
    currentTab,
    teamId,
    accountId,
    isProfile,
    isPeoplePage,
    isCompetitions,
    isLMSHSE,
    isMe,
    isFeedPage,
    isLectureMedia,
    isSchool,
    designerId,
    preloadedTabs,
    teachersOnly
  ])

  useEffect(() => {
    const tabsLoaded = !!Object.keys(preloadedTabs as {}).length;
    const alreadyLoaded = !!((isChannel ? (prevTeam === teamId) : (prevTeam === preloadedTabsContext))
      && !initialLoadedRef.current
      && !contextName?.startsWith('profile')) && tabsLoaded;
    if (!alreadyLoaded && tabsLoaded && preloadedTabsContext === contextName) {
      loadProjects()
      setInitialLoaded(true)
    }
  }, [
    time,
    teamId,
    prevTeam,
    preloadedTabsContext,
    contextName,
    accountId,
    preloadedTabs,
    isChannel,
    teamId,
  ])

  useEffectSkipFirst(() => {
    loadProjects()
  }, [
    currentCategory,
    filter?.projectFilter,
    filter?.hasProductCards,
    filter?.moduleFormat,
    filter?.countryCode,
    filter?.competitionStatus,
    filter?.requiredTagIds,
    filter?.sort,
    filter?.accountId,
    filter?.byTagIds,
    teachersOnly
  ])

  useEffect(() => {
    if (initialLoaded) {
      initialLoadedRef.current = true
    }
  }, [initialLoaded])

  useEffect(() => {
    dispatch(ProjectsGridActions.setCurrentTeam(isChannel ? teamId : contextName))
    return () => {
      if (initialLoadedRef.current) {
        dispatch(ProjectsGridActions.setPrevTeam(isChannel ? teamId : contextName))
      }
    }
  }, [teamId, contextName])


  const handleUpdate = useCallback(() => {
    if (currentTab === DesignerTab.bookmarks) {
      reloadAllTabs();
    }
  }, [currentTab, reloadAllTabs])

  const onSetWideCoverProject = (project: Project) => {
    dispatch(WideCoverSlice.actions?.setWideProject(project))
  }

  const shouldVisibleDraftsGrid = useMemo(
    () => (isDrafts || !!['draft_books', 'drafts']?.includes(currentTab)), [isDrafts, currentTab])

  const isChannels = useMemo(() => {
    return currentTab === 'edu-channels' || currentTab === 'channels' || entityType === 'educationSubscribes'
  } , [currentTab, entityType])

  const canCreateProjectsIntoDrafts = useMemo(() => {
    return !isAgeyTomesh && !isStart && !isGreenAward && !isDafes && !isHH && !isHseCreative && !isLMSHSE
  }, [])

  const translatedProjects = useMemo(() => {
    return data?.data ? API.toLang<Project[]>(data?.data, language) : []
  }, [data?.data, language]);

  const sortedOrganizations = useMemo(() => {
    return translatedProjects;
  }, [translatedProjects]);


  useEffect(() => {
    if (!translatedProjects.length && currentTab === 'teachers') {
      setFirstPageLoaded(true)
    }
  }, [translatedProjects.length, currentTab])

  const shouldViewWideCover = useMemo(
    () => (isMedia && translatedProjects?.length
    ), [
    isMedia,
    translatedProjects,
    hasWideCover,

  ])

  const showEmptyTabMsg = useMemo(
    () =>
      !fetchingTabs &&
      initialLoaded &&
      !translatedProjects?.length,
    [
      initialLoaded,
      isProfile,
      translatedProjects,
      isCompetitions,
      fetchingTabs,
      isMedia,
      emptyMsgComponent,
      emptyMsgText
    ])

  const largeMobOffset = useMemo(() => (isProfile), [isProfile])

  const isAlreadyProject = useCallback((data: string | undefined) => {
    if (isParticipate && isParticipate.length > 0) {
      return data ? isParticipate.includes(data) : false;
    }
  }, [isParticipate])

  const fetchMoreProjectsNext = useCallback(() => {
    dispatch(fetchProjectsGridMore({
      currentTab,
      currentCategory,
      filter,
      accountId,
      isMe: isMe || !!isFeedPage,
      isChannel,
      isPeoplePage,
      isProfile,
      isCompetitions,
      isLMSHSE,
      isSchool,
      contextName,
      teachersOnly,
      isHseSpaceDesignerPage
    }));
  }, [
    i18n,
    dispatch,
    currentCategory,
    teachersOnly,
    showcase,
    accountId,
    fetchProjectsGridMore,
    currentTab,
    filter,
    isPageWithoutTabs,
    preloadedTabs,
    contextName
  ])

  useEffect(() => {
    if (shouldViewWideCover) {
      const proProject = translatedProjects?.find(
        (p: any) => p?.contexts?.includes('propresentation')
      )

      //@ts-ignore
      onSetWideCoverProject(proProject || undefined)
    }
  }, [
    shouldViewWideCover,
    onSetWideCoverProject,
    translatedProjects,
    filter?.projectFilter,
    filter?.currentCategory,
    currentTab,
    currentCategory
  ])

  const ShowMoreButtonOrLoading = useMemo(() => {

    if (!fetchingMore) {
      return null;
    }

    return (
      <FlexBox direction='column' directionMobile='column' width="100%">
        {/* @ts-ignore */}
        <GridWrapperNew mobileColumns={2} itemType={isAcademy && isMap && 'mapItem'}>
          {new Array(8).fill('').map((_, index) => {
            return isAcademy && isMap ? <SkeletonCardMap key={index} /> : <SkeletonCard key={index} />
          })}
        </GridWrapperNew>
        <FlexBox justify="center" offsetBottom={24} offsetTop={32}>
          <ProgressBar
            text={t('text.loading')}
            loading={true}
            color={theme.progressbar.color}
          />
        </FlexBox>
      </FlexBox>
    )
  }, [t, theme, fetchingMore])

  if (hideProjects) {
    return null;
  }

  if (entityType === 'competitions') {
    return (
      <FlexBox direction="column" directionMobile="column" gy={40} flex="1">
        <DesignerCompetitionsTab
          onNotParticipateCompetition={handlers?.onNotParticipateCompetition}
          //@ts-ignore
          competitions={translatedProjects as ParticipateCompetitions}
        />
      </FlexBox>
    )
  }

  return (
    <ScrollWrapper direction="column" directionMobile="column" gy={isMedia ? 0 : 40} flex="1">
      <InfiniteScroll
        style={scrollStyles}
        dataLength={translatedProjects?.length}
        next={fetchMoreProjectsNext}
        hasMore={!isFinalPage}
        initialScrollY={0}
        scrollThreshold={0.9}
        loader={ShowMoreButtonOrLoading}
        scrollableTarget={scrollableTarget}
        onScroll={onScroll}
      >{isChannels ? (
        <ChannelsGrid
          isEditable={false}
          //@ts-ignore
          teams={translatedProjects.map(computeTeam)}
          contextName={contextName}
          entityType={entityType}
          onTeamClick={(team?: ComputedTeam, e?: any) => {
            e.preventDefault();
            e.stopPropagation();

            let url = team?.route;

            if (isMainPageHseSpace) {
              // Преобразуем URL для главной страницы https://hse.space/
              url = team?.route?.replace('/teams/s-', '/');
            } else {
              url = process.env.REACT_APP_URL_MEDIIIA_COM + '/teams/' + team?.teamId
            }

            window.open(url, '_blank');
          }}
          hasLinks
        />
      ) :entityType === 'organizations' ? (
        <TeamGrid
          key={`organizations-${filter?.sort || 3}`}
          isEditable={false}
          isLoading={fetchingTabs}
          //@ts-ignore
          teams={sortedOrganizations.map(computeTeam)}
          contextName={contextName}
          entityType={entityType}
          hasLinks
        />
      ) : ['people', 'people_in_detail', 'subscribers', 'subscriptions']?.includes(entityType) ? (
        <PeoplePageGrid
          language={i18n.language}
          isMe={(isMe || !!isFeedPage)}
          inDetail={entityType === 'people_in_detail'}
          data={translatedProjects as unknown as Designer<'Local'>[]}
          showOpa={showOpa}
          showTags
          twoMobileColumns={['subscribers', 'subscriptions']?.includes(entityType)}
          isOpaCard={isOpaCard}
          isLoading={loading}
          isMobileMoreVisible={['subscribers', 'subscriptions']?.includes(entityType)}
          {...handlers}
          tooltip={
            [
              DesignerTab.subscriptions]
              ?.includes(entityType) && (!!isMe || !!isFeedPage)
          }
        />
      ) : (entityType === 'jury' || entityType === 'competitions_new') ? (
        <CompetitionGrid
          // @ts-ignore
          competitions={translatedProjects}
          isProfile={isProfile}
          isMainPage={isMainPage}
        />
      ) : entityType === 'discount' ? (
        <DesignerDiscountTab data={translatedProjects} isLoading={fetchingTabs} />
      ) : entityType === 'competitionCerts' ? (
        <DesignerDiscountTab data={translatedProjects} isLoading={fetchingTabs} isCert={true} />
      ) : shouldVisibleDraftsGrid ? (
        <DraftsGrid
          canCreateProjects={canCreateProjectsIntoDrafts}
          entityType={entityType}
          projects={translatedProjects}
          activeTabKey={currentTab}
          {...handlers} />
      ) : contextName === 'creativecityacademymap' && isMapPage ? (
        // @ts-ignore
        <PopupInfoNodeGrid isLoading={fetchingTabs}>{
          translatedProjects?.map((v: Nullable<Partial<Project<'Local'>>>, index: number) => {
            // @ts-ignore
            const project = mapProjectToMapObject(v)
            return (
              <PopUpInfoNode
                loading={fetchingTabs}
                titleImage={project.titleImage}
                title={project.title}
                designer={project.designer}
                project={project.project}
                key={`${project.projectId}_section`}
                likes={project.likes}
                designers={[project.project.author].concat(project.project.coAuthors)}
                competitionId={project.competitionId}
                projectId={project.projectId}
                designerId={project.designerId}
                fixedSizeCard={true}
              />
            )
          })}
        </PopupInfoNodeGrid>
      ) : (
        isCompetitions && currentTab !== 'winners' ? <></>
          :
          <ProjectsGridNew k={isMainPage ? 20 : undefined} isLoading={fetchingTabs} columns={columns} mobileColumns={mobileColumns} largeMobOffset={largeMobOffset} isSuperPuperGames={!!isSuperPuperGames}>
            {translatedProjects?.map((v: Nullable<Partial<Project<'Local'>>>, index: number) => {
              if (filter?.isPaid || !!preloadedTabs?.main?.find((x: any) => x.path == currentTab)?.filter?.isPaid) {
                //@ts-ignore
                const price = v?.additionalInfo?.['productPrice'] ?? ''
                return (
                  // @ts-ignore
                  <ShopCard
                    project={v}
                    customClick={() => window.open(CreateLinkTo.book(v?.projectId ?? ''), '_self')}
                    key={v?.projectId}
                    id={v?.projectId}
                    images={v?.titleImages}
                    name={Lang(v?.title, i18n.language)}
                    price={price}
                    isBook
                  />
                )
              }
              if (!v?.isBook) {
                return (
                  // @ts-ignore
                  <ProjectCardNew
                    placement={placement}
                    isChannel={isChannel}
                    isParticipant={isChannelParticipant}
                    withoutRepost={withoutRepost}
                    project={v as any}
                    isModal={modal}
                    entityType={entityType}
                    withoutGoToProject={withoutGoToProject}
                    onProjectClick={modal ?
                      (projectId?: string, e?: any) => {
                        e?.stopPropagation();
                        e?.preventDefault();
                      } : undefined}
                    isSelectable={selectable}
                    isEditable={
                      !isAgeyTomesh &&
                      ((v && designer && bProject.isEditableFor(v as any, designer)) ||
                        bProfile.isModer(designer as any) || bProfile.isMiddleModer(designer as any))
                      && !BookDelegate?.isPaid(v as any)
                    }
                    //@ts-ignore
                    isRejected={ProjectDelegate.isRejectedProject(v)}
                    key={(v?.projectId || '') + index}
                    {...computeProject(v)}
                    onShouldReload={reloadAllTabs}
                    onUpdate={handleUpdate}
                    onSelect={onSelect}
                    selected={selectedProjects?.includes(v?.projectId ?? '')}
                    alreadyProject={isAlreadyProject(v?.projectId)}
                    linkPrefix={linkPrefix ? linkPrefix : undefined}
                    showCompetitionPoint={showCompetitionPoint}
                    canMakeImageForTeam={canMakeImageForTeam}
                  />
                );
              } else {
                const book = computeBookProject(v);
                return (
                  // @ts-ignore
                  <BookCard
                    book={book as any}
                    isChannel={isChannel}
                    isParticipant={isChannelParticipant}
                    entityType={entityType}
                    key={(book?.bookId || '') + index}
                    {...computeBook(book)}
                    onShouldReload={reloadAllTabs}
                    linkPrefix={linkPrefix ? linkPrefix : undefined}
                  />
                );
              }
            })}
          </ProjectsGridNew>
      )}
      </InfiniteScroll>
      <EmptyCardsLoading k={isMainPage ? 0 : 8} isLoading={fetchingTabs} />
      {showEmptyTabMsg && (emptyMsgComponent || <EmptyCategory text={emptyMsgText} />)}
      <OffsetBlock display={(!paging?.pages || paging?.pages === 1)} />
    </ScrollWrapper>
  );
};

export default memo(ProjectsButchControllerUltimate)
import { FC, useEffect, useState, useMemo, memo, useCallback, useRef, ReactNode, lazy } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { Nullable, Project, API, ParticipateCompetitions, Lang, Designer } from '@brg/api';
import { useProfile, bProfile } from '@brg/auth';
import { FlexBox } from '@brg/ui';
import InfiniteScroll from 'react-infinite-scroll-component';

import { ChannelsGrid, GridWrapperNew, ProjectsGridNew, TeamGrid } from '../../shared/components/grids';
import ProjectCardNew from '../../shared/components/projectCard/projectCardNew';
import { computeProject } from '../../shared/helpers/types/computed/ComputedProject';
import { bProject } from '../utils';
import useQueryStateOrigin from "../../shared/hooks/useQueryState";
import { useTranslation } from 'react-i18next';
import { ProgressBar } from 'shared/icons/Progress';
import styled, { useTheme } from 'styled-components';
import { AppTester } from "supporting";
import { BookCard } from '_deziiign/books/core/components/bookCard';
import { computeBook, computeBookProject } from '_deziiign/books/core/components/helper/types/computed/ComputedBook';
import { EmptyCardsLoading } from 'shared/components/CardsLoading/EmptyCards';
import { SkeletonCard, SkeletonCardMap } from 'shared/components/SkeletonCard/SkeletonCard';
import { ProjectDelegate } from 'project-delegate';
import { TOwnerRights } from 'shared/pages/exploreHseContextsNew/exploreHseContextsNew';
import { ProjectsGridActions, ProjectsGridSelector } from "../../shared/reducers/ProjectsGridSlice";
import { fetchProjectsGrid, fetchProjectsGridMore } from "../../shared/actions/projectsGridActions";
import { ComputedTeam, computeTeam } from "../../shared/helpers/types/computed/ComputedTeam";
import { DesignerCompetitionsTab } from "../../shared/pages/designer/components/tabs/designerCompetitionsTab";
import { CombinedEvents } from "../../shared/helpers/projectEvents";
import { DraftsGrid } from "../../shared/pages/designer/components/draftsGrid";
import { DesignerTab } from "../../shared/constants/designerTab";
import { ModerationType } from "../../shared/pages/teams/const/ModerationTypes";
import CompetitionGrid from "../../_competition/pages/AllCompetitionsPage/CompetitionGrid";
import { BookDelegate } from "../../_deziiign/books/core/book-delegate";
import WideCoverSlice, { WideCoverSelector } from 'shared/reducers/WideCoverSlice';
import { EmptyCategory } from 'shared/components/EmptyCategory/EmptyCategory';
import { ShopCard } from 'shared/pages/Shop/ShopCard';
import { CreateLinkTo, LinkTo } from 'shared/constants/linkTo';
import { DesignerDiscountTab } from 'shared/pages/designer/components/tabs/designerDiscountstab';
import { useParams, useRouteMatch as useRouteMatchOrigin } from "react-router-dom";
import { PeoplePageGrid } from 'shared/pages/context/support/Grid';
import { mapProjectToMapObject } from 'shared/pages/ResearchPage/Research/Frames/FramesSsr/mapProjectToMapObject';
import { isServer } from 'shared/constants/isServer';
import { useParamsSsr, useRouteMatchSsr } from 'shared/hooks/useRouteMatchSsr';
import { ProfileFoldersProps, ProfileFoldersSelector } from 'shared/reducers/ProfileFoldersSlice';
import { Placement } from 'shared/components/tooltips';
import useEffectSkipFirst from 'core/hooks/useEffectSkipFirst';

const useRouteMatch = isServer ? useRouteMatchSsr : useRouteMatchOrigin
const useQueryState = isServer ? () => [{ teachersOnly: false, showcase: false, folderId: false }] : useQueryStateOrigin
const PopupInfoNodeGrid = isServer ? FlexBox : lazy(() => import('shared/pages/ResearchPage/Research/Components/Popup/stylesSsr/PopupInfoNodeGrid'))
const PopUpInfoNode = isServer ? FlexBox : lazy(() => import('../../shared/pages/ResearchPage/Research/Components/Popup/PopUpInfoNode'))
const usedUseParams = isServer ? useParamsSsr : useParams
const useParamsProps = isServer ? LinkTo.designer : undefined

export const StyledLoading = styled.div`
  margin-bottom: 40px;
  padding: 10px;
  font-size: 60px;
  line-height: 1;
  color: white;
  text-align: center;
`;

export const ShowMoreWrapper = styled(FlexBox)`
  margin-left: auto;
  margin-right: auto;
  border: 1px solid ${p => p.theme.progressbar.borderColor};
  border-radius: 3px;
  padding: 12px 48px;
`;

const OffsetBlock = styled.div<{ display: boolean }>`
  width: 100%;
  margin-top: 64px;
  display: ${p => p?.display ? 'inline-block' : 'none'};
`;

const ScrollWrapper = styled(FlexBox)`
  .infinite-scroll-component {
    overflow: visible !important;
  }
`

type Tabs = {
  [key: string]: string[] | string | null;
}

export interface ProjectsButchControllerProps<T = Project> {
  filter: any;
  onProjectsLoaded?: (projects: T[]) => void;
  selectable?: boolean;
  onSelect?: (projectId: string) => void;
  withoutGoToProject?: boolean;
  withoutRepost?: boolean;
  selectedProjects?: string[];
  isParticipate?: string[];
  modal?: boolean;
  linkPrefix?: string;
  projectsCount?: number;
  isLoading?: Function;
  isSelectRightNeed?: boolean;
  columns?: number;
  isUnload?: boolean;
  preloadedTabs?: Tabs[];
  emptyMsgComponent?: ReactNode;
  currentTab: string;
  currentCategory: string;
  contextName?: string;
  isPageWithoutTabs?: boolean;
  emptyMsgText?: string;
  showCompetitionPoint?: boolean;
  isChannelParticipant?: boolean;
  isTeamProjects?: boolean;
  isDisabled?: boolean;
  canMakeImageForTeam?: TOwnerRights;
  isMainPage?: boolean;
  isProfile?: boolean;
  isPeoplePage?: boolean;
  isCompetitions?: boolean;
  handlers?: CombinedEvents;
  moderationType?: ModerationType;
  loading?: boolean;
  teamId?: string;
  accountId?: string;
  mobileColumns?: number;
  scrollableTarget?: any;
  isMe?: number;
  scrollStyles?: any;
  showOpa?: boolean;
  channelOwnerId?: string;
  isChannel?: boolean;
  isOpaCard?: boolean;
  isMobileMoreVisible?: boolean;
  placement?: Placement;
  onScroll?: (e: any) => void;
}

const ProjectsButchControllerUltimate: FC<ProjectsButchControllerProps> = ({
  filter,
  selectable = false,
  modal = false,
  onSelect = () => undefined,
  withoutGoToProject,
  selectedProjects = [],
  isParticipate = [],
  linkPrefix,
  columns,
  isPageWithoutTabs,
  withoutRepost,
  contextName,
  teamId,
  accountId,
  currentTab,
  scrollStyles,
  currentCategory,
  showCompetitionPoint,
  canMakeImageForTeam,
  isProfile,
  isPeoplePage,
  isCompetitions,
  isMainPage,
  handlers,
  mobileColumns,
  loading,
  emptyMsgComponent,
  isChannelParticipant,
  emptyMsgText,
  scrollableTarget,
  isMe,
  showOpa,
  isOpaCard,
  isChannel,
  channelOwnerId,
  placement,
  onScroll
}) => {
  const designer = useProfile();
  const theme = useTheme();
  const isMapPage = useRouteMatch('/map');
  const isFeedPage = useRouteMatch('/feed');

  const [{ teachersOnly, showcase = false, folderId }] = useQueryState();
  const { id: designerId } = usedUseParams(useParamsProps)

  const dispatch = useDispatch();
  const isAgeyTomesh = AppTester.isAgeytomesh();
  const isMedia = AppTester.isMediiia();
  const isLMSHSE = AppTester.isLMSHSE();
  const isGreenAward = AppTester.isGreenAward();
  const isDafes = AppTester.isGeoDafes();
  const isHH = AppTester.isContext('hsehh');
  const isHseCreative = AppTester.isLMSHSECreative();
  const isLectureMedia = AppTester.isLectureMediiia();
  const isSchool = AppTester.isSchool()
  const isStart = AppTester.isStartHSE();
  const isAcademy = AppTester.isAcademy();
  const isMap = !!useRouteMatch('/map');
  const isExactPage = !!useRouteMatch('/');
  const isMainPageHseSpace = AppTester.isContext('hsespace') && isExactPage;
  const isHseSpaceDesignerPage = AppTester.isContext('hsespace') && !!useRouteMatch('/designer/:id')

  const { t, i18n, i18n: { language } } = useTranslation();
  const [time, setTime] = useState(Date.now());
  const [initialLoaded, setInitialLoaded] = useState(false)
  const initialLoadedRef = useRef(false)
  const [firstPageLoaded, setFirstPageLoaded] = useState(false)

  const prevUserId = useRef<any>(null);

  const hasWideCover = useSelector(WideCoverSelector)?.project;
  const preloadedTabs = useSelector(ProjectsGridSelector)?.tabs;
  const preloadedTabsContext = useSelector(ProjectsGridSelector)?.context;
  const data = useSelector(ProjectsGridSelector)?.data?.[currentCategory]?.[currentTab];
  const fetchingTabs = useSelector(ProjectsGridSelector)?.tabsLoading?.[currentCategory]?.[currentTab];
  const fetchingMore = useSelector(ProjectsGridSelector)?.fetchingMore;
  const prevTeam = useSelector(ProjectsGridSelector)?.prevTeam;
  const currentFolder = useSelector(ProfileFoldersSelector)?.currentFolder;
  //@ts-ignore
  const currentTeam = useSelector((state) => state?.organizations)?.team;
  
  const isSuperPuperGames = useMemo(
    () => ((currentTeam?.contextName  === 'superpupergames-mediiia'))
  , [currentTeam]);

  const hideProjects = useMemo(() => {
    return ([ProfileFoldersProps.create, ProfileFoldersProps.edit]?.includes(currentFolder) || folderId) && isProfile
  }, [folderId, isProfile, currentFolder])

  const reloadAllTabs = useCallback(() => {
    setTime(Date.now());
    handlers?.onUpdate?.();
  }, [handlers])

  const { isFinalPage, paging, entityType, isDrafts } = data || {};

  useEffect(() => {
    if (isProfile && prevUserId.current && prevUserId.current !== designerId) {
      dispatch(ProjectsGridActions.resetProjects())
    }

    prevUserId.current = designerId
  }, [prevUserId, designerId, dispatch, isProfile])

  const loadProjects = useCallback(() => {
    dispatch(fetchProjectsGrid({
      filter,
      isChannel,
      channelOwnerId,
      currentCategory,
      contextName,
      currentTab,
      teamId,
      accountId,
      designerId,
      teachersOnly,
      isMe: (isMe || !!isFeedPage),
      isHseSpaceDesignerPage,

      //todo [МУСОР] удалить
      isLMSHSE,
      isProfile,
      isPeoplePage,
      isCompetitions,
      isLectureMedia,
      isSchool,
    }))
  }, [
    filter,
    isChannel,
    channelOwnerId,
    currentCategory,
    contextName,
    currentTab,
    teamId,
    accountId,
    isProfile,
    isPeoplePage,
    isCompetitions,
    isLMSHSE,
    isMe,
    isFeedPage,
    isLectureMedia,
    isSchool,
    designerId,
    preloadedTabs,
    teachersOnly
  ])

  useEffect(() => {
    const tabsLoaded = !!Object.keys(preloadedTabs as {}).length;
    const alreadyLoaded = !!((isChannel ? (prevTeam === teamId) : (prevTeam === preloadedTabsContext))
      && !initialLoadedRef.current
      && !contextName?.startsWith('profile')) && tabsLoaded;
    if (!alreadyLoaded && tabsLoaded && preloadedTabsContext === contextName) {
      loadProjects()
      setInitialLoaded(true)
    }
  }, [
    time,
    teamId,
    prevTeam,
    preloadedTabsContext,
    contextName,
    accountId,
    preloadedTabs,
    isChannel,
    teamId,
  ])

  useEffectSkipFirst(() => {
    loadProjects()
  }, [
    currentCategory,
    filter?.projectFilter,
    filter?.hasProductCards,
    filter?.moduleFormat,
    filter?.countryCode,
    filter?.competitionStatus,
    filter?.requiredTagIds,
    filter?.sort,
    filter?.accountId,
    filter?.byTagIds,
    teachersOnly
  ])

  useEffect(() => {
    if (initialLoaded) {
      initialLoadedRef.current = true
    }
  }, [initialLoaded])

  useEffect(() => {
    dispatch(ProjectsGridActions.setCurrentTeam(isChannel ? teamId : contextName))
    return () => {
      if (initialLoadedRef.current) {
        dispatch(ProjectsGridActions.setPrevTeam(isChannel ? teamId : contextName))
      }
    }
  }, [teamId, contextName])


  const handleUpdate = useCallback(() => {
    if (currentTab === DesignerTab.bookmarks) {
      reloadAllTabs();
    }
  }, [currentTab, reloadAllTabs])

  const onSetWideCoverProject = (project: Project) => {
    dispatch(WideCoverSlice.actions?.setWideProject(project))
  }

  const shouldVisibleDraftsGrid = useMemo(
    () => (isDrafts || !!['draft_books', 'drafts']?.includes(currentTab)), [isDrafts, currentTab])

  const isChannels = useMemo(() => {
    return currentTab === 'edu-channels' || currentTab === 'channels' || entityType === 'educationSubscribes'
  } , [currentTab, entityType])

  const canCreateProjectsIntoDrafts = useMemo(() => {
    return !isAgeyTomesh && !isStart && !isGreenAward && !isDafes && !isHH && !isHseCreative && !isLMSHSE
  }, [])

  const translatedProjects = useMemo(() => {
    return data?.data ? API.toLang<Project[]>(data?.data, language) : []
  }, [data?.data, language]);

  const sortedOrganizations = useMemo(() => {
    return translatedProjects;
  }, [translatedProjects]);


  useEffect(() => {
    if (!translatedProjects.length && currentTab === 'teachers') {
      setFirstPageLoaded(true)
    }
  }, [translatedProjects.length, currentTab])

  const shouldViewWideCover = useMemo(
    () => (isMedia && translatedProjects?.length
    ), [
    isMedia,
    translatedProjects,
    hasWideCover,

  ])

  const showEmptyTabMsg = useMemo(
    () =>
      !fetchingTabs &&
      initialLoaded &&
      !translatedProjects?.length,
    [
      initialLoaded,
      isProfile,
      translatedProjects,
      isCompetitions,
      fetchingTabs,
      isMedia,
      emptyMsgComponent,
      emptyMsgText
    ])

  const largeMobOffset = useMemo(() => (isProfile), [isProfile])

  const isAlreadyProject = useCallback((data: string | undefined) => {
    if (isParticipate && isParticipate.length > 0) {
      return data ? isParticipate.includes(data) : false;
    }
  }, [isParticipate])

  const fetchMoreProjectsNext = useCallback(() => {
    dispatch(fetchProjectsGridMore({
      currentTab,
      currentCategory,
      filter,
      accountId,
      isMe: isMe || !!isFeedPage,
      isChannel,
      isPeoplePage,
      isProfile,
      isCompetitions,
      isLMSHSE,
      isSchool,
      contextName,
      teachersOnly,
      isHseSpaceDesignerPage
    }));
  }, [
    i18n,
    dispatch,
    currentCategory,
    teachersOnly,
    showcase,
    accountId,
    fetchProjectsGridMore,
    currentTab,
    filter,
    isPageWithoutTabs,
    preloadedTabs,
    contextName
  ])

  useEffect(() => {
    if (shouldViewWideCover) {
      const proProject = translatedProjects?.find(
        (p: any) => p?.contexts?.includes('propresentation')
      )

      //@ts-ignore
      onSetWideCoverProject(proProject || undefined)
    }
  }, [
    shouldViewWideCover,
    onSetWideCoverProject,
    translatedProjects,
    filter?.projectFilter,
    filter?.currentCategory,
    currentTab,
    currentCategory
  ])

  const ShowMoreButtonOrLoading = useMemo(() => {

    if (!fetchingMore) {
      return null;
    }

    return (
      <FlexBox direction='column' directionMobile='column' width="100%">
        {/* @ts-ignore */}
        <GridWrapperNew mobileColumns={2} itemType={isAcademy && isMap && 'mapItem'}>
          {new Array(8).fill('').map((_, index) => {
            return isAcademy && isMap ? <SkeletonCardMap key={index} /> : <SkeletonCard key={index} />
          })}
        </GridWrapperNew>
        <FlexBox justify="center" offsetBottom={24} offsetTop={32}>
          <ProgressBar
            text={t('text.loading')}
            loading={true}
            color={theme.progressbar.color}
          />
        </FlexBox>
      </FlexBox>
    )
  }, [t, theme, fetchingMore])

  if (hideProjects) {
    return null;
  }

  if (entityType === 'competitions') {
    return (
      <FlexBox direction="column" directionMobile="column" gy={40} flex="1">
        <DesignerCompetitionsTab
          onNotParticipateCompetition={handlers?.onNotParticipateCompetition}
          //@ts-ignore
          competitions={translatedProjects as ParticipateCompetitions}
        />
      </FlexBox>
    )
  }

  return (
    <ScrollWrapper direction="column" directionMobile="column" gy={isMedia ? 0 : 40} flex="1">
      <InfiniteScroll
        style={scrollStyles}
        dataLength={translatedProjects?.length}
        next={fetchMoreProjectsNext}
        hasMore={!isFinalPage}
        initialScrollY={0}
        scrollThreshold={0.9}
        loader={ShowMoreButtonOrLoading}
        scrollableTarget={scrollableTarget}
        onScroll={onScroll}
      >{isChannels ? (
        <ChannelsGrid
          isEditable={false}
          //@ts-ignore
          teams={translatedProjects.map(computeTeam)}
          contextName={contextName}
          entityType={entityType}
          onTeamClick={(team?: ComputedTeam, e?: any) => {
            e.preventDefault();
            e.stopPropagation();

            let url = team?.route;

            if (isMainPageHseSpace) {
              // Преобразуем URL для главной страницы https://hse.space/
              url = team?.route?.replace('/teams/s-', '/');
            } else {
              url = process.env.REACT_APP_URL_MEDIIIA_COM + '/teams/' + team?.teamId
            }

            window.open(url, '_blank');
          }}
          hasLinks
        />
      ) :entityType === 'organizations' ? (
        <TeamGrid
          key={`organizations-${filter?.sort || 3}`}
          isEditable={false}
          isLoading={fetchingTabs}
          //@ts-ignore
          teams={sortedOrganizations.map(computeTeam)}
          contextName={contextName}
          entityType={entityType}
          hasLinks
        />
      ) : ['people', 'people_in_detail', 'subscribers', 'subscriptions']?.includes(entityType) ? (
        <PeoplePageGrid
          language={i18n.language}
          isMe={(isMe || !!isFeedPage)}
          inDetail={entityType === 'people_in_detail'}
          data={translatedProjects as unknown as Designer<'Local'>[]}
          showOpa={showOpa}
          showTags
          twoMobileColumns={['subscribers', 'subscriptions']?.includes(entityType)}
          isOpaCard={isOpaCard}
          isLoading={loading}
          isMobileMoreVisible={['subscribers', 'subscriptions']?.includes(entityType)}
          {...handlers}
          tooltip={
            [
              DesignerTab.subscriptions]
              ?.includes(entityType) && (!!isMe || !!isFeedPage)
          }
        />
      ) : (entityType === 'jury' || entityType === 'competitions_new') ? (
        <CompetitionGrid
          // @ts-ignore
          competitions={translatedProjects}
          isProfile={isProfile}
          isMainPage={isMainPage}
        />
      ) : entityType === 'discount' ? (
        <DesignerDiscountTab data={translatedProjects} isLoading={fetchingTabs} />
      ) : entityType === 'competitionCerts' ? (
        <DesignerDiscountTab data={translatedProjects} isLoading={fetchingTabs} isCert={true} />
      ) : shouldVisibleDraftsGrid ? (
        <DraftsGrid
          canCreateProjects={canCreateProjectsIntoDrafts}
          entityType={entityType}
          projects={translatedProjects}
          activeTabKey={currentTab}
          {...handlers} />
      ) : contextName === 'creativecityacademymap' && isMapPage ? (
        // @ts-ignore
        <PopupInfoNodeGrid isLoading={fetchingTabs}>{
          translatedProjects?.map((v: Nullable<Partial<Project<'Local'>>>, index: number) => {
            // @ts-ignore
            const project = mapProjectToMapObject(v)
            return (
              <PopUpInfoNode
                loading={fetchingTabs}
                titleImage={project.titleImage}
                title={project.title}
                designer={project.designer}
                project={project.project}
                key={`${project.projectId}_section`}
                likes={project.likes}
                designers={[project.project.author].concat(project.project.coAuthors)}
                competitionId={project.competitionId}
                projectId={project.projectId}
                designerId={project.designerId}
                fixedSizeCard={true}
              />
            )
          })}
        </PopupInfoNodeGrid>
      ) : (
        isCompetitions && currentTab !== 'winners' ? <></>
          :
          <ProjectsGridNew k={isMainPage ? 20 : undefined} isLoading={fetchingTabs} columns={columns} mobileColumns={mobileColumns} largeMobOffset={largeMobOffset} isSuperPuperGames={!!isSuperPuperGames}>
            {translatedProjects?.map((v: Nullable<Partial<Project<'Local'>>>, index: number) => {
              if (filter?.isPaid || !!preloadedTabs?.main?.find((x: any) => x.path == currentTab)?.filter?.isPaid) {
                //@ts-ignore
                const price = v?.additionalInfo?.['productPrice'] ?? ''
                return (
                  // @ts-ignore
                  <ShopCard
                    project={v}
                    customClick={() => window.open(CreateLinkTo.book(v?.projectId ?? ''), '_self')}
                    key={v?.projectId}
                    id={v?.projectId}
                    images={v?.titleImages}
                    name={Lang(v?.title, i18n.language)}
                    price={price}
                    isBook
                  />
                )
              }
              if (!v?.isBook) {
                return (
                  // @ts-ignore
                  <ProjectCardNew
                    placement={placement}
                    isChannel={isChannel}
                    isParticipant={isChannelParticipant}
                    withoutRepost={withoutRepost}
                    project={v as any}
                    isModal={modal}
                    entityType={entityType}
                    withoutGoToProject={withoutGoToProject}
                    onProjectClick={modal ?
                      (projectId?: string, e?: any) => {
                        e?.stopPropagation();
                        e?.preventDefault();
                      } : undefined}
                    isSelectable={selectable}
                    isEditable={
                      !isAgeyTomesh &&
                      ((v && designer && bProject.isEditableFor(v as any, designer)) ||
                        bProfile.isModer(designer as any) || bProfile.isMiddleModer(designer as any))
                      && !BookDelegate?.isPaid(v as any)
                    }
                    //@ts-ignore
                    isRejected={ProjectDelegate.isRejectedProject(v)}
                    key={(v?.projectId || '') + index}
                    {...computeProject(v)}
                    onShouldReload={reloadAllTabs}
                    onUpdate={handleUpdate}
                    onSelect={onSelect}
                    selected={selectedProjects?.includes(v?.projectId ?? '')}
                    alreadyProject={isAlreadyProject(v?.projectId)}
                    linkPrefix={linkPrefix ? linkPrefix : undefined}
                    showCompetitionPoint={showCompetitionPoint}
                    canMakeImageForTeam={canMakeImageForTeam}
                  />
                );
              } else {
                const book = computeBookProject(v);
                return (
                  // @ts-ignore
                  <BookCard
                    book={book as any}
                    isChannel={isChannel}
                    isParticipant={isChannelParticipant}
                    entityType={entityType}
                    key={(book?.bookId || '') + index}
                    {...computeBook(book)}
                    onShouldReload={reloadAllTabs}
                    linkPrefix={linkPrefix ? linkPrefix : undefined}
                  />
                );
              }
            })}
          </ProjectsGridNew>
      )}
      </InfiniteScroll>
      <EmptyCardsLoading k={isMainPage ? 0 : 8} isLoading={fetchingTabs} />
      {showEmptyTabMsg && (emptyMsgComponent || <EmptyCategory text={emptyMsgText} />)}
      <OffsetBlock display={(!paging?.pages || paging?.pages === 1)} />
    </ScrollWrapper>
  );
};

export default memo(ProjectsButchControllerUltimate)
